import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Package, ShoppingCart, Heart, Clock, Scissors, Users,
} from 'lucide-react';
import theme from '../../../styles/theme';
import useInView from '../../../hooks/useInView';

const features = [
  { icon: Package,     key: 'inventory',  color: theme.colors.primary.main },
  { icon: ShoppingCart,key: 'sales',      color: theme.colors.secondary.main },
  { icon: Heart,       key: 'pets',       color: '#C95B5B' },
  { icon: Clock,       key: 'daycare',    color: theme.colors.warning },
  { icon: Scissors,    key: 'grooming',   color: '#7B68EE' },
  { icon: Users,       key: 'users',      color: theme.colors.accent.main },
];

const FeaturesSection = () => {
  const { t } = useTranslation();
  const [ref, visible] = useInView();

  const s = {
    section: {
      padding: `${theme.spacing['3xl']} ${theme.spacing.xl}`,
      backgroundColor: theme.colors.background.default,
    },
    inner: {
      maxWidth: '1120px',
      margin: '0 auto',
    },
    header: {
      textAlign: 'center',
      marginBottom: theme.spacing['3xl'],
    },
    eyebrow: {
      display: 'inline-block',
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.semibold,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: theme.colors.primary.main,
      marginBottom: theme.spacing.md,
    },
    h2: {
      fontSize: theme.typography.fluid.h2,
      fontWeight: theme.typography.fontWeight.bold,
      fontFamily: theme.typography.fontFamily.heading,
      color: theme.colors.text.primary,
      marginBottom: theme.spacing.md,
    },
    sub: {
      fontSize: theme.typography.fontSize.lg,
      color: theme.colors.text.secondary,
      maxWidth: '540px',
      margin: '0 auto',
      lineHeight: theme.typography.lineHeight.relaxed,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(3, 1fr)',
      gap: theme.spacing.xl,
    },
    card: (i, visible) => ({
      backgroundColor: theme.colors.background.paper,
      borderRadius: theme.borderRadius.lg,
      padding: theme.spacing.xl,
      boxShadow: theme.shadows.sm,
      border: `1px solid ${theme.colors.border.light}`,
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0)' : 'translateY(24px)',
      transition: `opacity 0.5s ease ${i * 80}ms, transform 0.5s ease ${i * 80}ms`,
    }),
    iconWrap: (color) => ({
      width: '48px',
      height: '48px',
      borderRadius: theme.borderRadius.md,
      backgroundColor: `${color}18`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginBottom: theme.spacing.md,
    }),
    cardTitle: {
      fontSize: theme.typography.fontSize.lg,
      fontWeight: theme.typography.fontWeight.semibold,
      color: theme.colors.text.primary,
      marginBottom: theme.spacing.sm,
    },
    cardDesc: {
      fontSize: theme.typography.fontSize.sm,
      color: theme.colors.text.secondary,
      lineHeight: theme.typography.lineHeight.relaxed,
    },
    learnMore: {
      display: 'inline-block',
      marginTop: theme.spacing.md,
      fontSize: theme.typography.fontSize.sm,
      fontWeight: theme.typography.fontWeight.medium,
      color: theme.colors.primary.main,
      textDecoration: 'none',
    },
  };

  return (
    <section style={s.section}>
      <div style={s.inner}>
        <div style={s.header}>
          <span style={s.eyebrow}>{t('public.features.eyebrow', 'Everything you need')}</span>
          <h2 style={s.h2}>{t('public.features.heading', 'One platform, every module')}</h2>
          <p style={s.sub}>{t('public.features.sub', 'From the first product scan to the last invoice — Waggy covers the full operational cycle of your pet store.')}</p>
        </div>

        <div style={s.grid} ref={ref} className="features-grid">
          {features.map(({ icon: Icon, key, color }, i) => (
            <div key={key} style={s.card(i, visible)}>
              <div style={s.iconWrap(color)}>
                <Icon size={22} color={color} />
              </div>
              <div style={s.cardTitle}>{t(`public.features.${key}.title`, key)}</div>
              <div style={s.cardDesc}>{t(`public.features.${key}.desc`, '')}</div>
              <Link to="/features" style={s.learnMore}
                onMouseOver={(e) => (e.target.style.textDecoration = 'underline')}
                onMouseOut={(e) => (e.target.style.textDecoration = 'none')}
              >
                {t('public.features.learnMore', 'Learn more →')}
              </Link>
            </div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) { .features-grid { grid-template-columns: repeat(2,1fr) !important; } }
        @media (max-width: 600px) { .features-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
};

export default FeaturesSection;
